import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  Trash2,
  Truck,
  Scale,
  FileText,
  ClipboardList,
  FlaskConical,
  Boxes,
  Factory,
  Receipt,
  Building2,
  LayoutDashboard,
  ExternalLink,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useNotificationsFeed } from "../hooks/useNotificationsFeed";

const NOTIF_ICONS = {
  gate: Truck,
  weighbridge: Scale,
  contracts: FileText,
  purchases: ClipboardList,
  lab: FlaskConical,
  stacks: Boxes,
  production: Factory,
  accounts: Receipt,
  parties: Building2,
  dashboard: LayoutDashboard,
};

const TONE_DOT = {
  gold: "bg-bansal-gold",
  sage: "bg-bansal-sage",
  warning: "bg-amber-500",
  info: "bg-sky-500",
  danger: "bg-red-500",
  default: "bg-muted-foreground/50",
};

function timeAgo(ts) {
  const s = Math.max(1, Math.round((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function NotificationsInbox({ align = "end", triggerClassName = "" }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items, unread, markAllRead, clearAll } = useNotificationsFeed();

  const onOpenChange = (next) => {
    setOpen(next);
    if (next && unread > 0) {
      // brief delay so the badge is visible on open, then mark read
      setTimeout(() => markAllRead(), 1200);
    }
  };

  const openItem = (item) => {
    setOpen(false);
    if (item.route) navigate(item.route);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Notifications"
          data-testid="notifications-trigger"
          className={`relative inline-grid place-items-center h-9 w-9 rounded-lg border border-border text-foreground/70 hover:text-foreground hover:bg-muted transition-colors ${triggerClassName}`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold grid place-items-center leading-none tabular"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={10}
        className="w-[min(92vw,26rem)] p-0 overflow-hidden"
        data-testid="notifications-panel"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/40">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-foreground">Notifications</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={markAllRead}
              title="Mark all read"
              className="inline-grid place-items-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <CheckCheck className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={clearAll}
              title="Clear all"
              className="inline-grid place-items-center h-7 w-7 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="max-h-[min(70vh,26rem)] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center">
              <div className="mx-auto mb-3 h-11 w-11 rounded-full bg-muted grid place-items-center text-muted-foreground">
                <Bell className="h-5 w-5" />
              </div>
              <p className="text-sm font-medium text-foreground">You&apos;re all caught up</p>
              <p className="text-xs text-muted-foreground mt-1">Live activity across the mill shows up here.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((item) => {
                const Icon = NOTIF_ICONS[item.iconKey] || LayoutDashboard;
                return (
                  <li key={item.key}>
                    <button
                      type="button"
                      onClick={() => openItem(item)}
                      className={`group w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/60 transition-colors ${
                        item.read ? "" : "bg-bansal-sageLight/25 dark:bg-bansal-sage/10"
                      }`}
                    >
                      <span className="mt-0.5 shrink-0 h-8 w-8 rounded-lg bg-muted grid place-items-center text-primary">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          {!item.read && (
                            <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${TONE_DOT[item.tone] || TONE_DOT.default}`} />
                          )}
                          <span className="text-sm font-medium text-foreground truncate">{item.title}</span>
                          {item.route ? (
                            <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground/50 group-hover:text-primary" />
                          ) : null}
                        </span>
                        {item.description ? (
                          <span className="block text-xs text-muted-foreground mt-0.5 line-clamp-3 leading-relaxed">{item.description}</span>
                        ) : null}
                        <span className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-muted-foreground/70">{timeAgo(item.at)}</span>
                          {item.route ? (
                            <span className="text-[10px] text-primary/80 opacity-0 group-hover:opacity-100 transition-opacity">
                              Open
                            </span>
                          ) : null}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
